import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/i5z1wfjq_WTFH%20logo.png';

const CAROUSEL_IMAGES = [
  { img: 'https://images.pexels.com/photos/5784811/pexels-photo-5784811.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Spot the next big stock move',          color: '#34D399', glow: 'rgba(52,211,153,0.55)'  },
  { img: 'https://images.pexels.com/photos/5972330/pexels-photo-5972330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Know when to buy or sell shares',       color: '#60A5FA', glow: 'rgba(96,165,250,0.55)'  },
  { img: 'https://images.pexels.com/photos/11279904/pexels-photo-11279904.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Catch Bitcoin moves before they happen', color: '#F7931A', glow: 'rgba(247,147,26,0.65)'  },
  { img: 'https://images.pexels.com/photos/11387203/pexels-photo-11387203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Track which crypto is heating up',      color: '#FB923C', glow: 'rgba(251,146,60,0.60)'  },
  { img: 'https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',   label: 'Find what sells on TikTok Shop',       color: '#F472B6', glow: 'rgba(244,114,182,0.60)' },
  { img: 'https://images.pexels.com/photos/13457491/pexels-photo-13457491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Know which sneakers to flip',          color: '#C084FC', glow: 'rgba(192,132,252,0.55)' },
  { img: 'https://images.pexels.com/photos/7857532/pexels-photo-7857532.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Scale your side hustle faster',        color: '#2DD4BF', glow: 'rgba(45,212,191,0.55)'  },
  { img: 'https://images.pexels.com/photos/9661254/pexels-photo-9661254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Trade the trending cards',              color: '#FACC15', glow: 'rgba(250,204,21,0.60)'  },
  { img: 'https://images.unsplash.com/photo-1726572330396-0947f571ac19?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',   label: 'Spot undervalued gems early',          color: '#22D3EE', glow: 'rgba(34,211,238,0.60)'  },
  { img: 'https://images.pexels.com/photos/6068975/pexels-photo-6068975.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Sell vintage before it peaks',          color: '#FB7185', glow: 'rgba(251,113,133,0.55)' },
];

export default function SplashScreen({ onContinue }) {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    document.body.classList.add('dark');
    localStorage.setItem('wtf_dark_mode', 'true');
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      data-testid="splash-screen"
      className="fixed inset-0 overflow-hidden flex flex-col items-center justify-center cursor-pointer"
      style={{ background: '#0d0d0d' }}
      onClick={onContinue}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.4 }}
    >
      {/* Image carousel — caption above, then rectangular rounded image */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, type: 'spring', stiffness: 120 }}
        className="relative z-10 mb-6 mx-4 flex flex-col items-center"
        style={{ width: 'calc(100% - 32px)', maxWidth: 400 }}
      >
        {/* Caption — above image, centred, bigger */}
        <div
          className="w-full mb-3 px-2 flex items-center justify-center text-center"
          style={{ minHeight: '3.5rem' }}
        >
          <AnimatePresence mode="wait">
            <motion.p
              key={activeImage}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="text-2xl font-extrabold leading-tight tracking-tight"
              style={{
                fontFamily: 'Manrope, sans-serif',
                color: CAROUSEL_IMAGES[activeImage].color,
                // Double-layer glow: tight inner glow for punch + wide outer halo for depth
                textShadow: `
                  0 0 1px ${CAROUSEL_IMAGES[activeImage].color},
                  0 0 12px ${CAROUSEL_IMAGES[activeImage].glow},
                  0 0 28px ${CAROUSEL_IMAGES[activeImage].glow},
                  0 2px 8px rgba(0,0,0,0.6)
                `,
              }}
              data-testid="splash-carousel-caption"
            >
              {CAROUSEL_IMAGES[activeImage].label}
            </motion.p>
          </AnimatePresence>
        </div>

        <div
          className="relative w-full rounded-2xl overflow-hidden"
          style={{ aspectRatio: '4 / 3' }}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={activeImage}
              src={CAROUSEL_IMAGES[activeImage].img}
              alt={CAROUSEL_IMAGES[activeImage].label}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
          {/* Dot indicators */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 pb-1">
            {CAROUSEL_IMAGES.map((_, i) => (
              <div key={i} className="w-1 h-1 rounded-full transition-all duration-300"
                style={{ background: i === activeImage ? '#fff' : 'rgba(255,255,255,0.3)' }} />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Free / Premium info — coloured value props with soft glow halos */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="z-10 mb-6 text-center space-y-1 mx-4 px-6 py-5 rounded-2xl"
      >
        <p
          className="text-2xl font-semibold"
          style={{
            color: '#34D399',
            textShadow: '0 0 12px rgba(52,211,153,0.55), 0 0 24px rgba(52,211,153,0.35)',
          }}
        >
          News personalised for you
        </p>
        <div className="h-3" />
        <p
          className="text-2xl font-semibold"
          style={{
            color: '#38BDF8',
            textShadow: '0 0 12px rgba(56,189,248,0.55), 0 0 24px rgba(56,189,248,0.35)',
          }}
        >
          Casual Investors &amp; Resellers
        </p>
        <p
          className="text-2xl font-semibold"
          style={{
            color: '#C084FC',
            textShadow: '0 0 12px rgba(192,132,252,0.55), 0 0 24px rgba(192,132,252,0.35)',
          }}
        >
          What's Trending, Bullish, What's Selling
        </p>
      </motion.div>

      {/* CTA button with logo */}
      <motion.button
        data-testid="splash-cta"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={(e) => { e.stopPropagation(); onContinue(); }}
        className="px-8 py-3 rounded-full z-10 shadow-lg flex items-center gap-2"
        style={{
          background: '#FFFFFF',
          boxShadow: '0 8px 30px rgba(255, 255, 255, 0.2)',
        }}
      >
        <img src={LOGO_URL} alt="WTFHappened" className="h-20 object-contain" />
      </motion.button>
    </motion.div>
  );
}
